import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { collection, query, where, onSnapshot, getDocs, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

const orderSteps = ['Sipariş Alındı', 'Hazırlanıyor', 'Kargoya Verildi', 'Teslim Edildi'];

const statusColors = {
  'Bekliyor': 'warning',
  'Onaylandı': 'info',
  'Hazırlanıyor': 'primary',
  'Kargoya Verildi': 'secondary',
  'Tamamlandı': 'success',
  'İptal Edildi': 'error'
};

function MyOrders() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);

  const handleCancelOrder = async () => {
    try {
      const orderRef = doc(db, 'orders', orderToCancel);
      await updateDoc(orderRef, {
        status: 'İptal Edildi',
        cancelledAt: new Date().toISOString()
      });
      setSuccess('Siparişiniz başarıyla iptal edildi');
      setOpenCancelDialog(false);
      setOrderToCancel(null);
    } catch (error) {
      setError('Sipariş iptal edilirken bir hata oluştu');
    }
  };

  const openCancelConfirmation = (orderId) => {
    setOrderToCancel(orderId);
    setOpenCancelDialog(true);
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (!currentUser?.uid) {
          setLoading(false);
          return;
        }

        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('userId', '==', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          const ordersData = snapshot.docs.map(doc => {
            const data = doc.data();
            
            const orderItems = data.items || data.products || [];
            
            return {
              id: doc.id,
              ...data,
              products: orderItems.map(item => ({
                id: item.id || item.productId,
                image: item.image,
                title: item.title || 'Ürün',
                quantity: item.quantity || 1,
                price: item.price || 0,
                selectedSize: item.size || item.selectedSize || '',
                selectedColor: item.color || item.selectedColor || ''
              })),
              createdAt: (data.createAt || data.createdAt)?.toDate() || new Date(),
              status: data.status || 'Bekliyor',
              total: data.totalAmount || 0
            };
          });

          ordersData.sort((a, b) => b.createdAt - a.createdAt);

          console.log('Bulunan Siparişler:', ordersData);
          setOrders(ordersData);
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Siparişler yüklenirken hata:', error);
        setError('Siparişler yüklenirken bir hata oluştu');
        setLoading(false);
      }
    };

    fetchOrders();
  }, [currentUser]);

  // Sipariş durumuna göre adım indeksini belirleyen yardımcı fonksiyon
  const getStepIndex = (status) => {
    const statusMap = {
      'Bekliyor': 0,
      'Onaylandı': 0,
      'Hazırlanıyor': 1,
      'Kargoya Verildi': 2,
      'Teslim Edildi': 3,
      'İptal Edildi': -1
    };
    return statusMap[status] ?? 0;
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        minHeight: '60vh'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Container>
    );
  }

  if (orders.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4, mt: { xs: '56px', sm: '64px' } }}>
        <Card elevation={0} sx={{ bgcolor: '#f8f8f8', borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" gutterBottom>
              Henüz siparişiniz bulunmuyor
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Alışverişe başlayarak yeni siparişler oluşturabilirsiniz.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ 
      py: 4, 
      mt: { xs: '80px', sm: '90px' },
      minHeight: 'calc(100vh - 80px)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 4 }}>
        Siparişlerim
      </Typography>

      <Grid container spacing={3} sx={{ flexGrow: 1, alignContent: 'flex-start' }}>
        {orders.map((order) => (
          <Grid item xs={12} key={order.id}>
            <Card elevation={0} sx={{ bgcolor: '#f8f8f8', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Sipariş No: {order.id}
                  </Typography>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tarih: {order.createdAt?.toLocaleDateString('tr-TR')}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  {order.products?.map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: 2,
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start'
                      }}
                    >
                      {/* Ürün Görseli */}
                      <Box
                        sx={{
                          width: 80,
                          height: 100,
                          flexShrink: 0,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: 'grey.100',
                          border: '1px solid',
                          borderColor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = 'Görsel Yüklenemedi';
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              textAlign: 'center',
                              p: 1
                            }}
                          >
                            Görsel Bulunamadı
                          </Typography>
                        )}
                      </Box>

                      {/* Ürün Detayları */}
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                          {item.title || 'Ürün'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {item.quantity || 1} adet
                          {item.selectedSize && ` - Beden: ${item.selectedSize}`}
                          {item.selectedColor && ` - Renk: ${item.selectedColor}`}
                        </Typography>
                        <Typography variant="body2" color="primary" fontWeight="500">
                          {((item.price || 0) * (item.quantity || 1)).toLocaleString('tr-TR')} TL
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Sipariş Durumu
                  </Typography>
                  <Chip
                    label={order.status}
                    color={order.status === 'İptal Edildi' ? 'error' : 'default'}
                    sx={{ 
                      mt: 1,
                      backgroundColor: order.status === 'İptal Edildi' ? '#ffebee' : undefined,
                      color: order.status === 'İptal Edildi' ? '#d32f2f' : undefined
                    }}
                  />
                  {order.status === 'İptal Edildi' && (
                    <Typography 
                      variant="caption" 
                      color="error"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {order.cancelledAt ? `İptal Tarihi: ${new Date(order.cancelledAt).toLocaleDateString('tr-TR')}` : 'İptal Edildi'}
                    </Typography>
                  )}
                </Box>

                {order.status === 'Bekliyor' && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => openCancelConfirmation(order.id)}
                    sx={{ mt: 2 }}
                  >
                    Siparişi İptal Et
                  </Button>
                )}

                <Box sx={{ 
                  mt: 3,
                  pt: 2,
                  borderTop: '1px dashed rgba(0, 0, 0, 0.12)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Toplam Tutar
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {order.total?.toLocaleString('tr-TR', { 
                      style: 'currency', 
                      currency: 'TRY' 
                    })}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Sipariş İptali</DialogTitle>
        <DialogContent>
          Siparişinizi iptal etmek istediğinizden emin misiniz?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>Vazgeç</Button>
          <Button onClick={handleCancelOrder} color="error" variant="contained">
            İptal Et
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default MyOrders; 